/**
 * @fileoverview Parse SSE `data:` JSON lines from portal messages API stream into typed chunks.
 * @purpose Pure parsing extracted from ChatInstance for lower CRAP and unit tests.
 */

export type ToolCallPayload = {
  id?: string;
  name?: string;
  function?: {
    name?: string;
  };
  arguments?: unknown;
  result?: unknown;
  success?: boolean;
  error?: string;
};

export type StreamChunk =
  | { type: 'content'; content: string }
  | { type: 'tool_call_message'; toolCall: ToolCallPayload }
  | { type: 'tool_call_update'; toolCall: ToolCallPayload }
  | { type: 'tool_call_error'; callId: string; error?: string }
  | { type: 'done' }
  | { type: 'error'; error?: string };

export function isToolCallPayload(value: unknown): value is ToolCallPayload {
  if (!value || typeof value !== 'object') return false;
  const maybe = value as Record<string, unknown>;
  return (
    (!maybe.id || typeof maybe.id === 'string') &&
    (!maybe.name || typeof maybe.name === 'string') &&
    (!maybe.function || typeof (maybe.function as { name?: unknown }).name === 'string') &&
    (!maybe.arguments || typeof maybe.arguments !== 'undefined') &&
    (!maybe.result || typeof maybe.result !== 'undefined') &&
    (!maybe.success || typeof maybe.success === 'boolean') &&
    (!maybe.error || typeof maybe.error === 'string')
  );
}

export function parseStreamChunk(payload: string): StreamChunk | null {
  try {
    const data = JSON.parse(payload) as Record<string, unknown>;
    const type = data.type;
    if (type === 'content' && typeof data.content === 'string') {
      return { type, content: data.content };
    }
    if ((type === 'tool_call_message' || type === 'tool_call_update') && isToolCallPayload(data.toolCall)) {
      return { type, toolCall: data.toolCall };
    }
    if (type === 'tool_call_error' && typeof data.callId === 'string') {
      return { type, callId: data.callId, error: typeof data.error === 'string' ? data.error : undefined };
    }
    if (type === 'done') return { type };
    if (type === 'error') {
      return { type, error: typeof data.error === 'string' ? data.error : undefined };
    }
  } catch {
    return null;
  }
  return null;
}

/** True when payload looks like a single JSON object (filters noise before JSON.parse in hot path). */
export function looksLikeJsonObjectPayload(payload: string): boolean {
  const p = payload.trim();
  return p.startsWith('{') && (p.endsWith('}') || p.endsWith('}\n') || p.endsWith('}\r'));
}
